import { create } from "zustand";

export type AnomalySeverity = "info" | "warning" | "critical";

export interface Anomaly {
  id: string;
  severity: AnomalySeverity;
  message: string;
  contextKey?: string;
  actionableUrl?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

export type AICopilotEntityType =
  | "invoice"
  | "journal_entry"
  | "product"
  | "party"
  | "report"
  | "import_job"
  | null;

export interface ActiveEntityContext {
  type: AICopilotEntityType;
  id: string | null;
  label: string | null;
}

export interface FocusedCellContext {
  rowId: string | null;
  columnKey: string | null;
}

export interface ScreenSnapshot {
  viewType: "grid" | "form" | "dashboard";
  summaryStats: Record<string, unknown>;
  visibleData: Array<Record<string, unknown>>;
  focusedCell?: FocusedCellContext | null;
}

export interface SnapshotMeta {
  capturedAt: string | null;
  source: "route" | "grid_ref" | "anomaly_trigger" | "chat_submit" | "drawer_open" | null;
}

export interface AICopilotState {
  isOpen: boolean;
  isProcessing: boolean;
  currentRoute: string;
  activeEntity: ActiveEntityContext;
  screenSnapshot: ScreenSnapshot | null;
  snapshotMeta: SnapshotMeta;
  activeAnomalies: Anomaly[];
  messages: ChatMessage[];
  toggleCopilot: (forceState?: boolean) => void;
  setProcessing: (value: boolean) => void;
  updateRouteContext: (route: string) => void;
  updateEntityContext: (
    type: AICopilotEntityType,
    id: string | null,
    label: string | null,
  ) => void;
  updateScreenSnapshot: (
    snapshot: ScreenSnapshot | null,
    source?: SnapshotMeta["source"],
  ) => void;
  setAnomalies: (anomalies: Anomaly[]) => void;
  addMessage: (message: Omit<ChatMessage, "id" | "timestamp">) => void;
  clearConversation: () => void;
}

function makeId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().replace(/-/g, "")}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function normalizeVisibleData(
  rows: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> {
  return rows.slice(0, 20).map((row) => {
    const next: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      if (value === null || value === undefined) {
        next[key] = null;
        continue;
      }
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        next[key] = value;
        continue;
      }
      next[key] = String(value);
    }
    return next;
  });
}

export const useAICopilotStore = create<AICopilotState>((set) => ({
  isOpen: false,
  isProcessing: false,
  currentRoute: "",
  activeEntity: {
    type: null,
    id: null,
    label: null,
  },
  screenSnapshot: null,
  snapshotMeta: {
    capturedAt: null,
    source: null,
  },
  activeAnomalies: [],
  messages: [],
  toggleCopilot: (forceState) =>
    set((state) => ({
      isOpen: typeof forceState === "boolean" ? forceState : !state.isOpen,
    })),
  setProcessing: (value) =>
    set({
      isProcessing: value,
    }),
  updateRouteContext: (route) =>
    set((state) => (state.currentRoute === route ? state : { currentRoute: route })),
  updateEntityContext: (type, id, label) =>
    set((state) =>
      state.activeEntity.type === type &&
      state.activeEntity.id === id &&
      state.activeEntity.label === label
        ? state
        : {
            activeEntity: {
              type,
              id,
              label,
            },
          },
    ),
  updateScreenSnapshot: (snapshot, source = "route") =>
    set(() => ({
      screenSnapshot: snapshot
        ? {
            ...snapshot,
            visibleData: normalizeVisibleData(snapshot.visibleData),
          }
        : null,
      snapshotMeta: {
        capturedAt: snapshot ? nowIso() : null,
        source: snapshot ? source : null,
      },
    })),
  setAnomalies: (anomalies) =>
    set({
      activeAnomalies: anomalies,
    }),
  addMessage: (message) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: makeId("aicmsg"),
          timestamp: nowIso(),
          ...message,
        },
      ],
    })),
  clearConversation: () =>
    set({
      messages: [],
    }),
}));

